/**
 * Savings Goal Calculator Page
 * Plan how to reach your savings goals with regular contributions
 */
import { useState, useEffect } from 'react';
import { useCustomTranslation } from '../../hooks';
import { trackPageView } from '../../utils/analytics';
import { Box, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import AmazonRecommendation from '../../components/common/AmazonRecommendation';


interface SavingsGoalData {
  goal_amount: number;
  current_savings: number;
  monthly_contribution: number;
  annual_interest_rate: number;
}

interface SavingsGoalResults {
  months_to_goal: number;
  years_to_goal: number;
  total_contributed: number;
  total_interest_earned: number;
  final_amount: number;
}

export default function SavingsGoalPage() {
  const { t, i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const [formData, setFormData] = useState<SavingsGoalData>({
    goal_amount: 0,
    current_savings: 0,
    monthly_contribution: 0,
    annual_interest_rate: 2.5,
  });
  const [results, setResults] = useState<SavingsGoalResults | null>(null);

  useEffect(() => {
    trackPageView('/calculators/savings-goal', 'Savings Goal Calculator');
    document.title = `${t('calculators.savingsGoal.title')} | PayWise365`;
  }, [t]);

  const handleCalculate = () => {
    const monthlyRate = formData.annual_interest_rate / 100 / 12;
    let balance = formData.current_savings;
    let months = 0;
    const maxMonths = 600; // 50 years max

    // Calculate months needed
    while (balance < formData.goal_amount && months < maxMonths) {
      balance = balance * (1 + monthlyRate) + formData.monthly_contribution;
      months++;
    }

    const total_contributed = formData.current_savings + (formData.monthly_contribution * months);
    const total_interest_earned = balance - total_contributed;
    const years_to_goal = months / 12;

    setResults({
      months_to_goal: months,
      years_to_goal,
      total_contributed,
      total_interest_earned,
      final_amount: balance,
    });
  };

  return (
    <div className="calculator-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{t('calculators.savingsGoal.title')}</h1>
          <p>{t('calculators.savingsGoal.pageSubtitle')}</p>
        </div>
      </div>
      
      <main className="calculator-content">
        <div className="calculator-container">
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('form.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                <Box>
                  <TextField
                    fullWidth
                    label="Goal Amount (HKD)"
                    type="number"
                    value={formData.goal_amount || ''}
                    onChange={(e) => setFormData({ ...formData, goal_amount: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Current Savings (HKD)"
                    type="number"
                    value={formData.current_savings || ''}
                    onChange={(e) => setFormData({ ...formData, current_savings: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Monthly Contribution (HKD)"
                    type="number"
                    value={formData.monthly_contribution || ''}
                    onChange={(e) => setFormData({ ...formData, monthly_contribution: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Annual Interest Rate (%)"
                    type="number"
                    value={formData.annual_interest_rate}
                    onChange={(e) => setFormData({ ...formData, annual_interest_rate: parseFloat(e.target.value) || 0 })}
                  />
                </Box>
                <Box>
                  <Button variant="contained" fullWidth onClick={handleCalculate} size="large">
                    Calculate
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {results && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  Results
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Time to Reach Goal</Typography>
                      <Typography variant="h5">{results.years_to_goal.toFixed(1)} years</Typography>
                      <Typography variant="body2">({results.months_to_goal} months)</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'success.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Total Contributed</Typography>
                      <Typography variant="h5">HKD {results.total_contributed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'info.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Interest Earned</Typography>
                      <Typography variant="h5">HKD {results.total_interest_earned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Box sx={{ p: 2, bgcolor: 'warning.main', borderRadius: 2, color: 'white' }}>
                      <Typography variant="body2">Final Amount</Typography>
                      <Typography variant="h5">HKD {results.final_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
      <div style={{ width: '100%', padding: '30px 20px' }}>
        <AmazonRecommendation isEnglish={isEnglish} />
      </div>
    </div>
  );
}
